import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { queryFeatureFilterCounts, queryFeatureSummary } from '@/features/features/data';
import { useCreateFeature } from '@/features/features/hooks/features';
import { entityFormAdapters, type FeatureFormValues } from '@/features/forms';
import {
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { buildFeatureCounts } from '@/features/quality/components/qualityStatusDataHelpers';
import { useReleasesQuery } from '@/features/releases/hooks/releases';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import { buildDeliveryPageFilters } from '@/features/shared/utils/deliveryPageFilters';
import { useNotifications } from '@/providers/notificationHooks';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import { getToastMessage } from '@/shared/copy/toasts';
import {
  gridPaths,
  queryKeys,
  useIsAuthenticated,
  useMutationErrorSnackbar,
  useOrganisationEntitlements,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildContainsFilter,
  buildEqFilter,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput, type GridQueryInput } from '@/ui/composites/grid/gridUtils';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { TagOption } from '@/shared/data/tags';
import type { FieldErrorProps } from '@/ui/hooks/useFormErrors';
import type { FeatureStatus } from '@flowqa/shared';

export type FeatureDeliveryMode = 'inProgress' | 'released';

const inProgressFeatureStatuses: FeatureStatus[] = ['draft', 'planned', 'ready'];
const releasedFeatureStatuses: FeatureStatus[] = ['released'];

const featureModeStatuses: Record<FeatureDeliveryMode, FeatureStatus[]> = {
  inProgress: inProgressFeatureStatuses,
  released: releasedFeatureStatuses,
};

type FeatureAddDialogState = {
  open: boolean;
  saving: boolean;
  values: FeatureFormValues;
  errors: { name?: FieldErrorProps };
  errorMessage: string | null;
  projectOptions: Array<{ id: string; name: string }>;
  releaseOptions: NonNullable<ReturnType<typeof useReleasesQuery>['data']>['items'];
  showProjectSelect: boolean;
  releaseDisabled: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSave: () => void;
  handlers: {
    onNameChange: (value: string) => void;
    onStatusChange: (value: FeatureStatus) => void;
    onProjectChange: (value: string) => void;
    onReleaseChange: (value: string) => void;
    onPlannedForChange: (value: string) => void;
    onReleasedAtChange: (value: string) => void;
    onTagsChange: (value: string[]) => void;
  };
};

type CountMap = Partial<Record<string, number>>;

const applyCountsToOptions = <TOption extends { value: string }>(
  options: readonly TOption[],
  counts: CountMap | undefined,
): TOption[] => {
  if (counts === undefined) return options.map((option) => ({ ...option }));
  return options.map((option) => ({
    ...option,
    count: counts[option.value] ?? 0,
  }));
};

type UseFeaturesPageViewModelResult = {
  activeMode: FeatureDeliveryMode;
  setActiveMode: (mode: FeatureDeliveryMode) => void;
  modeCounts: Record<FeatureDeliveryMode, number>;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedStatuses: FeatureStatus[];
  selectedTags: string[];
  selectedProjects: string[];
  tagOptions: TagOption[];
  tagOptionsLoading: boolean;
  statusOptions: Array<{ value: FeatureStatus; label: string; count?: number }>;
  projectFilterOptions: Array<{ value: string; label: string; count?: number }>;
  showProjectFilter: boolean;
  onStatusToggle: (status: FeatureStatus) => void;
  onStatusesChange: (nextStatuses: FeatureStatus[]) => void;
  onTagsChange: (nextTags: string[]) => void;
  onProjectsChange: (nextProjects: string[]) => void;
  tableFilters: GridQueryInput['filters'];
  featureMetricCards: ReturnType<typeof buildStatusMetricCards>;
  featureMetricCardsLoading: boolean;
  showPageLoading: boolean;
  isKnownEmpty: boolean;
  addDialog: FeatureAddDialogState;
};

export const useFeaturesPageViewModel = (): UseFeaturesPageViewModelResult => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectOptions,
    projectFilterOptions,
    selectedSidebarProjectId,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: selectedOrganisationId !== null });
  const [activeMode, setActiveModeState] = useState<FeatureDeliveryMode>('inProgress');
  const [searchValue, setSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<FeatureStatus[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:features');
  const [addOpen, setAddOpen] = useState(false);
  const addForm = useDialogFormState<FeatureFormValues>({
    name: '',
    projectId: effectiveProjectId ?? '',
    status: 'draft',
    releaseId: '',
    plannedFor: '',
    releasedAt: '',
    tags: [],
  });
  const addErrors = useFormErrors(addForm);
  const mutationError = useMutationErrorSnackbar();
  const createFeature = useCreateFeature();
  const { notify } = useNotifications();
  const addReleaseProjectId = addForm.values.projectId.trim() || effectiveProjectId;
  const releasesQuery = useReleasesQuery(addReleaseProjectId, {
    enabled: addReleaseProjectId !== null && addReleaseProjectId !== '',
  });
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const showDelta = false;
  const hasActiveFilters =
    searchValue.trim() !== '' ||
    selectedStatuses.length > 0 ||
    selectedTags.length > 0 ||
    (showProjectFilter && selectedProjects.length > 0);
  const activeModeStatuses = featureModeStatuses[activeMode];
  const modeStatusSet = useMemo(() => new Set(activeModeStatuses), [activeModeStatuses]);
  const tableStatusValues = useMemo(
    () =>
      selectedStatuses.length > 0
        ? selectedStatuses.filter((status) => modeStatusSet.has(status))
        : activeModeStatuses,
    [activeModeStatuses, modeStatusSet, selectedStatuses],
  );

  const searchFilters = useMemo(() => buildContainsFilter('name', searchValue), [searchValue]);
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const projectFilters = useMemo(
    () => (showProjectFilter ? buildInFilter('projectId', selectedProjects) : []),
    [selectedProjects, showProjectFilter],
  );
  const statusFilters = useMemo(
    () => buildInFilter('status', selectedStatuses),
    [selectedStatuses],
  );
  const tableStatusFilters = useMemo(
    () =>
      selectedStatuses.length > 0 && tableStatusValues.length === 0
        ? buildEqFilter('id', '__no_matching_feature_status__')
        : buildInFilter('status', tableStatusValues),
    [selectedStatuses.length, tableStatusValues],
  );
  const featureFilterCountGroups = useMemo(
    () => ({
      statuses: mergeFilters(searchFilters, tagFilters, projectFilters),
      tags: mergeFilters(searchFilters, statusFilters, projectFilters),
      projects: mergeFilters(searchFilters, statusFilters, tagFilters),
    }),
    [projectFilters, searchFilters, statusFilters, tagFilters],
  );
  const featureFilterCountKey = useMemo(
    () =>
      JSON.stringify({
        searchValue,
        selectedProjects,
        selectedStatuses,
        selectedTags,
      }),
    [searchValue, selectedProjects, selectedStatuses, selectedTags],
  );
  const featureFilterCountsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.features,
      'filter-option-counts',
      selectedOrganisationId ?? null,
      effectiveProjectId,
      featureFilterCountKey,
    ),
    enabled: isAuthenticated && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      await queryFeatureFilterCounts(effectiveProjectId, selectedOrganisationId, {
        search: searchValue.trim() || undefined,
        filters: featureFilterCountGroups,
      }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const statusOptions = useMemo(
    () => applyCountsToOptions(featureStatusOptions, featureFilterCountsQuery.data?.statuses),
    [featureFilterCountsQuery.data?.statuses],
  );
  const countedTagOptions = useMemo(
    () => applyCountsToOptions(tagOptions, featureFilterCountsQuery.data?.tags),
    [featureFilterCountsQuery.data?.tags, tagOptions],
  );
  const countedProjectFilterOptions = useMemo(
    () => applyCountsToOptions(projectFilterOptions, featureFilterCountsQuery.data?.projects),
    [featureFilterCountsQuery.data?.projects, projectFilterOptions],
  );
  const { summaryFilters } = useMemo(
    () =>
      buildDeliveryPageFilters({
        rangeStartIso: null,
        rangeEndIso: null,
        previousRangeStartIso: null,
        previousRangeEndIso: null,
        statusFilters: mergeFilters(statusFilters, searchFilters),
        tagFilters,
        projectFilters,
      }),
    [projectFilters, searchFilters, statusFilters, tagFilters],
  );
  const featureSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: summaryFilters,
      }),
    [summaryFilters],
  );
  const featureSummaryFiltersKey = useMemo(
    () => JSON.stringify(featureSummaryInput.filters),
    [featureSummaryInput.filters],
  );
  const featureSummaryQuery = useQuery({
    queryKey: queryKeys.featureSummary(
      effectiveProjectId,
      selectedOrganisationId ?? null,
      featureSummaryFiltersKey,
    ),
    enabled: isAuthenticated && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryFeatureSummary(
        effectiveProjectId,
        selectedOrganisationId ?? undefined,
        featureSummaryInput,
      ),
  });
  const featureSummary = featureSummaryQuery.data ?? null;
  const showPageLoading =
    featureSummaryQuery.isLoading && featureSummary === null && !featureSummaryQuery.isError;
  const isKnownEmpty =
    !showPageLoading &&
    deliveryEnabled &&
    featureSummary !== null &&
    featureSummary.total === 0 &&
    !hasActiveFilters;
  const featureCounts = useMemo(() => buildFeatureCounts(featureSummary), [featureSummary]);
  const modeCounts = useMemo(() => {
    const countStatuses = (statuses: FeatureStatus[]): number =>
      statuses
        .filter((status) => selectedStatuses.length === 0 || selectedStatuses.includes(status))
        .reduce((total, status) => total + (featureCounts[status] ?? 0), 0);

    return {
      inProgress: countStatuses(inProgressFeatureStatuses),
      released: countStatuses(releasedFeatureStatuses),
    };
  }, [featureCounts, selectedStatuses]);
  const featureMetricOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(featureStatusOptions, resolveMetricTone),
    [],
  );
  const featureMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: featureMetricOptions,
        counts: featureCounts,
        deltas: undefined,
        total: featureSummary?.total,
        totalLabel: 'Features',
        previousCounts: {},
        previousTotal: undefined,
        loading: featureSummaryQuery.isFetching && !featureSummaryQuery.isError,
        showDelta,
      }),
    [
      featureCounts,
      featureMetricOptions,
      featureSummary?.total,
      featureSummaryQuery.isError,
      featureSummaryQuery.isFetching,
      showDelta,
    ],
  );
  const tableFilters = useMemo(
    () => mergeFilters(searchFilters, tagFilters, projectFilters, tableStatusFilters),
    [projectFilters, searchFilters, tableStatusFilters, tagFilters],
  );
  const onStatusToggle = useCallback((status: FeatureStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);
  const onStatusesChange = useCallback((nextStatuses: FeatureStatus[]) => {
    setSelectedStatuses(nextStatuses);
  }, []);

  const setActiveMode = useCallback((mode: FeatureDeliveryMode) => {
    setActiveModeState(mode);
  }, []);

  const closeAddDialog = useCallback(() => {
    addForm.handleClose(
      () => {
        setAddOpen(false);
      },
      { clearError: mutationError.clearError },
    );
  }, [addForm, mutationError.clearError]);

  const openAddDialog = useCallback(() => {
    const defaultProjectId =
      selectedSidebarProjectId ??
      (selectedProjects.length === 1 ? (selectedProjects[0] ?? '') : '');
    addForm.setField('name', '');
    addForm.setField('projectId', defaultProjectId);
    addForm.setField('status', activeMode === 'released' ? 'released' : 'draft');
    addForm.setField('releaseId', '');
    addForm.setField('plannedFor', '');
    addForm.setField('releasedAt', '');
    addForm.setField('tags', []);
    mutationError.clearError();
    setAddOpen(true);
  }, [activeMode, addForm, mutationError, selectedProjects, selectedSidebarProjectId]);

  const saveFeature = useCallback(() => {
    const valid = selectedSidebarProjectId
      ? addForm.validateRequired('name')
      : addForm.validateRequired('name', 'projectId');
    if (!valid) return;
    const projectId = selectedSidebarProjectId ?? addForm.values.projectId;
    const payload = entityFormAdapters.feature.toCreatePayload(addForm.values, projectId);
    createFeature.mutate(payload, {
      onSuccess: () => {
        addForm.handleClose(
          () => {
            setAddOpen(false);
          },
          { clearError: mutationError.clearError },
        );
        notify({
          message: getToastMessage('entity.created', { entity: 'feature', name: payload.name }),
          tone: 'green',
        });
      },
      onError: (err) => {
        if (!addForm.applyApiErrors(err)) {
          mutationError.setError(err);
          return;
        }
        mutationError.clearError();
      },
    });
  }, [addForm, createFeature, mutationError, notify, selectedSidebarProjectId]);

  return {
    activeMode,
    setActiveMode,
    modeCounts,
    searchValue,
    setSearchValue,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions: countedTagOptions,
    tagOptionsLoading,
    statusOptions,
    projectFilterOptions: countedProjectFilterOptions,
    showProjectFilter,
    onStatusToggle,
    onStatusesChange,
    onTagsChange: setSelectedTags,
    onProjectsChange: handleProjectsChange,
    tableFilters,
    featureMetricCards,
    featureMetricCardsLoading: featureSummaryQuery.isFetching && !featureSummaryQuery.isError,
    showPageLoading,
    isKnownEmpty,
    addDialog: {
      open: addOpen,
      saving: createFeature.isPending,
      values: addForm.values,
      errors: { name: addErrors.fieldErrorProps('name') },
      errorMessage: addForm.submitError,
      projectOptions,
      releaseOptions: releasesQuery.data?.items ?? [],
      showProjectSelect: selectedSidebarProjectId === null,
      releaseDisabled: addReleaseProjectId === null || addReleaseProjectId === '',
      onOpen: openAddDialog,
      onClose: closeAddDialog,
      onSave: saveFeature,
      handlers: {
        onNameChange: (value) => {
          addForm.setField('name', value);
        },
        onStatusChange: (value) => {
          addForm.setField('status', value);
        },
        onProjectChange: (value) => {
          addForm.setField('projectId', value);
          addForm.setField('releaseId', '');
        },
        onReleaseChange: (value) => {
          addForm.setField('releaseId', value);
        },
        onPlannedForChange: (value) => {
          addForm.setField('plannedFor', value);
        },
        onReleasedAtChange: (value) => {
          addForm.setField('releasedAt', value);
        },
        onTagsChange: (value: string[]) => {
          addForm.setField('tags', value);
        },
      },
    },
  };
};
